/**
 * 面包屑导航组件
 * 显示当前页面的层级路径
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Home } from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useIsMobile } from '@/hooks/use-mobile'
import { SidebarTrigger } from '@/components/ui/sidebar'

interface BreadcrumbNavProps {
  /** 右侧操作区域（按钮等） */
  actions?: ReactNode
}

export function BreadcrumbNav({ actions }: BreadcrumbNavProps) {
  const breadcrumbs = useBreadcrumb()
  const isMobile = useIsMobile()

  // 如果没有面包屑数据，只显示侧边栏触发器
  if (breadcrumbs.length === 0) {
    return (
      <div className="flex h-14 items-center border-b px-4">
        <SidebarTrigger />
      </div>
    )
  }

  return (
    <div className="flex h-14 items-center border-b px-4 gap-4">
      <SidebarTrigger />
      <Breadcrumb>
        <BreadcrumbList>
          {/* 首页链接 */}
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={ROUTES.HOME} className="flex items-center gap-1">
                <Home className="h-4 w-4" />
                {!isMobile && <span>首页</span>}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>

          {/* 动态面包屑项 */}
          {breadcrumbs.map((item) => (
            <BreadcrumbItem key={item.label}>
              <BreadcrumbSeparator />
              {item.path ? (
                <BreadcrumbLink asChild>
                  <Link to={item.path}>{item.label}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{item.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      {/* 右侧操作按钮 */}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  )
}
